import React from "react";
import { useState } from "react";
import { requestRemoteTest, updateUserDeviceStatus } from '../../store/actions';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from "../ui/button";
import {
  RotateCw,
  RefreshCw,
  AlertTriangle,
  Circle,
  Ban,
  Stethoscope,
  Loader2
} from "lucide-react";

const ActionButtons = () => {
  const dispatch = useDispatch();

  const { userData: user } = useSelector(state => state.UserStatus);
  const isChromebookUser = !!user?.googleDeviceId;

  const googleDeviceIsActive = user?.googleDeviceStatus === 'ACTIVE';
	const googleDeviceIsDisabled = user?.googleDeviceStatus === 'DISABLED';
	const googleDeviceIsOperational = googleDeviceIsActive || googleDeviceIsDisabled;

  const [isTestRequested, setIsTestRequested] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  const triggerRemoteTest = () => {
    setIsTestRequested(true);
    dispatch(requestRemoteTest(user?.guid));
    
    const interval = setInterval(() => {
      clearInterval(interval);
      setIsTestRequested(false);
    }, 3000);
  };

  const handleAction = (action) => {
    setConfirmAction(action);
  };

  const getAction = (action) => {
    switch (action) {
      case 'Restart':
        return 'reboot';
      
      case 'Wipe & Re-enroll':
        return 'wipe';

      case 'Powerwash':
        return 'powerwash';

      case 'Disable':
        return 'disable';

      case 'Enable':
        return 'enable';
    }
  }
  
  const handleActionConfirm = (action) => {
    dispatch(updateUserDeviceStatus(user.googleDeviceId, getAction(action)));
    setConfirmAction(null);
  };

  return (
    <>
      <div className="flex flex-wrap gap-3 w-full md:w-auto">
        {/* Primary action buttons */}
        <div className="flex gap-2 items-center">
          <Button 
            onClick={triggerRemoteTest} 
            disabled={isTestRequested}
            size="sm"
            className="bg-indigo-600 hover:bg-indigo-700 min-w-[160px]"
          >
            {isTestRequested
            ? ( <> <Loader2 className="mr-1 h-4 w-4 animate-spin" /> Test Requested </> )
            : ( <> <Stethoscope className="mr-1 h-4 w-4" /> Request Test </> )}
          </Button>
        </div>
        
        {/* Secondary action buttons */}
        {isChromebookUser && (<div className="flex gap-2 items-center">
          {googleDeviceIsOperational && (<Button 
              variant="outline" 
              size="sm" 
              onClick={() => handleAction("Restart")}
            >
              <RotateCw className="mr-1 h-4 w-4" /> 
              Restart
          </Button>)}
          {googleDeviceIsOperational && (<Button 
            variant="outline" 
            size="sm" 
            onClick={() => handleAction("Wipe & Re-enroll")}
          >
            <RefreshCw className="mr-1 h-4 w-4" />
            Wipe & Re-enroll
          </Button>)}
        </div>)}

        {/* Secondary action buttons */}
        {isChromebookUser && (<div className="flex gap-2 items-center">
          {googleDeviceIsOperational && (<Button 
            variant="outline" 
            size="sm" 
            onClick={() => handleAction("Powerwash")}
          >
            <RefreshCw className="mr-1 h-4 w-4" />
            Powerwash
          </Button>)}
          {googleDeviceIsActive && (<Button 
            variant="outline" 
            size="sm" 
            onClick={() => handleAction("Disable")}
            className="text-red-500 hover:bg-red-50 hover:text-red-600"
          >
            <Ban className="mr-1 h-4 w-4" /> 
            Disable
          </Button>)}
          {googleDeviceIsDisabled && (<Button 
            variant="outline" 
            size="sm" 
            onClick={() => handleAction("Enable")}
          >
            <Circle className="mr-1 h-4 w-4" /> 
            Enable
          </Button>)}
        </div>)}
      </div>

      {/* Confirmation dialog */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-9999 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="h-6 w-6 text-yellow-500" />
              <h3 className="text-lg font-semibold">Confirm {confirmAction}</h3>
            </div>
            <p className="mb-6">Are you sure you want to {confirmAction.toLowerCase()} for {user?.email}? This action cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setConfirmAction(null)}>
                Cancel
              </Button>
              <Button onClick={() => handleActionConfirm(confirmAction)}>
                Confirm {confirmAction}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ActionButtons;
